import { getAndroidVersion, getIOSVersion } from '../../../../modules/AppInfo';
import nonCriticalException from '../../../../modules/exceptionLogger';
import { ShareTarget } from '../../../../modules/share';
import type { RootState } from '../../../../store/models';
import social from '../../../consumer/sharing/social';
import {
  ShareChannels,
  ShareOptions,
  SocialShareMessages,
  SocialShareUrls,
} from '../types';

type Channels = keyof SocialShareUrls;
type MessageParam = string | { text?: string; body?: string };

type ProfileShareOptions = {
  message: MessageParam;
  providerId: number | string;
  url: string;
  currentUser: RootState['user'];
  callback?: (event: { channel: Channels }) => void;
};

type ProfileShareFactoryOptions = {
  messages: SocialShareMessages;
  urls: SocialShareUrls;
  providerId: number | string;
  currentUser: RootState['user'];
  callback?: (event: { channel: Channels }) => void;
};

/**
 * Returns the list of different social channels in which a pro
 * may share their profile onto - based off their share urls list
 * @param socialShareUrls
 */
export const getShareChannelsForProShareProfile = (
  socialShareUrls: SocialShareUrls,
): ShareChannels[] => (
  Object.keys(socialShareUrls || {}).filter(channel => {
    // allow sms only on mobile app
    if (channel === 'sms') {
      return getIOSVersion() || getAndroidVersion();
    }

    // never allow pinterest
    return channel !== 'pinterest';
  })
) as ShareChannels[];

/**
 * Returns the list of different social channels in which a pro
 * may share their profile onto plus extra options that don't provide
 * a special social sharing function (like "embed").
 * @param socialShareUrls
 */
export const getShareOptionsForProShareProfile = (
  socialShareUrls: SocialShareUrls,
): ShareOptions[] => {
  const optionsFromShareURL = getShareChannelsForProShareProfile(socialShareUrls);

  if (!optionsFromShareURL) return [];

  return (
    [
      ...optionsFromShareURL,
      'instagram',
      'embed',
      'other',
    ] as ShareOptions[]
  );
};

/**
 * Fixes formatting differences in how a social media message is returned
 * @param messageParam
 */
const prepareMessage = (messageParam: MessageParam): { text: string } | undefined => {
  if (typeof messageParam === 'string') {
    return {
      text: messageParam,
    };
  }

  if (messageParam.body && !messageParam.text) {
    return {
      ...messageParam,
      text: messageParam.body,
    };
  }

  return undefined;
};

/**
 * Creates a social media share function for the provider's profile
 * for more info visit /modules/share.ts
 * @param channel
 * @param message
 * @param url
 * @param providerId
 * @param currentUser
 * @param callback
 */
export const createProfileShareFunction = (channel: Channels, {
  message,
  url,
  providerId,
  currentUser,
  callback,
}: ProfileShareOptions) => {
  const shareFunc = social[channel]?.[ShareTarget.PROFILE](
    { id: providerId },
    url,
    currentUser,
    () => {},
    () => {},
    prepareMessage(message),
  );

  if (!shareFunc) {
    nonCriticalException('Attempted social share on an unsupported channel', { channel });
    // eslint-disable-next-line no-console
    console.error(`Unsupported channel "${String(channel)}"`);
    return () => {};
  }

  return () => {
    callback?.({ channel });
    shareFunc();
  };
};

/**
 * Creates a social media share function for every channel provided
 * @param channels
 * @param messages
 * @param urls
 * @param providerId
 * @param currentUser
 * @param callback
 */
export const createAllProfileShareFunctions = (
  channels: Channels[],
  {
    messages,
    urls,
    providerId,
    currentUser,
    callback,
  }: ProfileShareFactoryOptions,
) => (channels || []).reduce((result, channelName) => ({
  ...result,
  [channelName]: createProfileShareFunction(channelName, {
    message: messages[channelName],
    url: urls[channelName],
    currentUser,
    providerId,
    callback,
  }),
}), {});
